// A site stock ledger for bulk consumables (cement, sand, aggregate) that are drawn from one common
// pile across the whole project, rather than bought for one activity at a time. Separate from — and
// built before — Allocation: this answers "how much is on site right now, and who took how much
// today", not "which estimate line did it go toward" (that link comes later).
import type { MaterialRateItem, ConcreteGrade } from '../pages/OfficeEstimationPage';

const round3 = (value: number) => Math.round((value + Number.EPSILON) * 1000) / 1000;
const num = (value: unknown) => { const n = Number(value); return Number.isFinite(n) ? n : 0; };

// ── Materials kept on a stock ledger ──────────────────────────────────────────
/** A material's own rate item can bundle several distinct commodities under one generic name
 * ("Concrete Materials" covering Sand, Aggregate 10mm, Aggregate 20mm as separate assembly-type
 * rows) — each row is tracked as its own line here. `rootId` is the underlying rate item's id (what
 * orders and receipts are actually keyed on); `id` is unique per row for a bundled item. */
export type TrackedMaterial = {
  id: string;
  rootId: string;
  /** Set only when this row is one of several bundled under the same rate item. */
  assemblyType?: string;
  label: string;
  unit: string;
  group: string;
  precision: 'exact' | 'tentative';
};

function rowLabel(item: Pick<MaterialRateItem, 'materialName'>, assemblyType: string, variety: string): string {
  if (assemblyType && variety && assemblyType.toLowerCase() !== variety.toLowerCase()) return `${assemblyType} · ${variety}`;
  return assemblyType || item.materialName;
}

const slug = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');

/** The same composite id `trackedMaterials` gives a bundled item's row — shared with Allocation so a
 * receipt's Stock-usage reference and its "new usage" badge key on the exact same row. */
export const materialKey = (rootId: string, assemblyType?: string) => assemblyType ? `${rootId}::${slug(assemblyType)}` : rootId;

export function trackedMaterials(materialItems: Pick<MaterialRateItem, 'id' | 'materialName' | 'unit' | 'group' | 'trackStock' | 'stockPrecision' | 'assemblyVarietyMap'>[]): TrackedMaterial[] {
  return materialItems
    .filter((item) => item.trackStock)
    .flatMap((item): TrackedMaterial[] => {
      const rows = item.assemblyVarietyMap || [];
      const precision = item.stockPrecision === 'tentative' ? 'tentative' : 'exact';
      if (rows.length <= 1) {
        const assemblyType = (rows[0]?.assemblyType || '').trim();
        const variety = (rows[0]?.varieties || [])[0]?.trim() || '';
        return [{ id: item.id, rootId: item.id, label: rowLabel(item, assemblyType, variety), unit: item.unit, group: String(item.group), precision }];
      }
      // A bundled item (several assembly types under one rate item) tracks each type separately.
      return rows.map((row) => {
        const assemblyType = (row.assemblyType || '').trim();
        const variety = (row.varieties || [])[0]?.trim() || '';
        return { id: materialKey(item.id, assemblyType || row.id), rootId: item.id, assemblyType, label: rowLabel(item, assemblyType, variety), unit: row.unit || item.unit, group: String(item.group), precision };
      });
    })
    .sort((a, b) => a.label.localeCompare(b.label));
}

// ── Which labour trade used the stock ─────────────────────────────────────────
/** A labour rate item from the Rates tab, e.g. "Tile Fixing Labour" — this is where issued stock gets
 * attributed, since it's the labour crew that actually draws material day to day. */
export type StockDestination = { id: string; label: string };

/**
 * Every labour rate item (Rates tab, group "labour") flagged to appear here — an admin curates the
 * list per labour item (`laborStockVisible`, defaulting to shown), independent of whether that trade
 * has a costed line in the estimate yet.
 */
export function laborDestinationOptions(materialItems: Pick<MaterialRateItem, 'id' | 'materialName' | 'group' | 'laborStockVisible'>[]): StockDestination[] {
  return materialItems
    .filter((item) => item.group === 'labour' && item.laborStockVisible !== false)
    .map((item) => ({ id: item.id, label: item.materialName }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function searchStockDestinations(options: StockDestination[], query: string): StockDestination[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return options;
  return options.filter((option) => words.every((word) => option.label.toLowerCase().includes(word)));
}

// ── The ledger itself ──────────────────────────────────────────────────────
export type StockIssue = {
  id: string;
  materialId: string;
  /** Set only when the issue is against one bundled row of a multi-assembly-type rate item. */
  materialAssemblyType?: string;
  materialLabel: string;
  unit: string;
  qty: number;
  date: string;
  time?: string;
  destination: StockDestination;
  note?: string;
  /** Shared by every issue saved together in one go (e.g. Cement plus its concrete-grade Sand/
   * Aggregate rows) — deleting one offers to remove the whole batch, so a correction is one action. */
  batchId?: string;
  recordedAt?: string;
};

/** Just enough of an order to walk its receipts — kept loose (receipts optional) so any of the app's
 * several order-row shapes can be passed in without a cast. */
export type StockOrderInput = {
  materialId?: string;
  materialAssemblyType?: string;
  unplannedRejected?: boolean;
  receipts?: Array<{ date: string; qty: number; reference?: string; billNumber?: string }>;
};

export const matchesRow = (rowType: string | undefined, assemblyType?: string) => !assemblyType || (rowType || '') === assemblyType;

/** Every receipt for one material across every order, oldest first. `assemblyType` narrows to one row
 * of a bundled item (e.g. just Sand, not the whole "Concrete Materials" item); omit it otherwise. */
export function receiptsForMaterial(
  orders: StockOrderInput[],
  materialId: string,
  assemblyType?: string,
): Array<{ date: string; qty: number; reference: string }> {
  const receipts: Array<{ date: string; qty: number; reference: string }> = [];
  for (const order of orders) {
    if (order.materialId !== materialId || order.unplannedRejected) continue;
    if (!matchesRow(order.materialAssemblyType, assemblyType)) continue;
    for (const receipt of order.receipts || []) {
      const qty = num(receipt.qty);
      if (qty <= 0) continue;
      receipts.push({ date: receipt.date, qty, reference: receipt.reference || receipt.billNumber || '' });
    }
  }
  return receipts.sort((a, b) => a.date.localeCompare(b.date));
}

export const totalReceived = (orders: StockOrderInput[], materialId: string, assemblyType?: string) =>
  round3(receiptsForMaterial(orders, materialId, assemblyType).reduce((sum, receipt) => sum + receipt.qty, 0));

export const totalIssued = (issues: Pick<StockIssue, 'materialId' | 'materialAssemblyType' | 'qty'>[], materialId: string, assemblyType?: string) =>
  round3(issues.filter((issue) => issue.materialId === materialId && matchesRow(issue.materialAssemblyType, assemblyType)).reduce((sum, issue) => sum + num(issue.qty), 0));

/** What's on site right now for a material: everything received, minus everything issued. Can go
 * negative — that's a real signal (wastage, an unlogged issue, a miscount), not something to hide. */
export function stockBalance(
  orders: StockOrderInput[],
  issues: Pick<StockIssue, 'materialId' | 'materialAssemblyType' | 'qty'>[],
  materialId: string,
  assemblyType?: string,
): number {
  return round3(totalReceived(orders, materialId, assemblyType) - totalIssued(issues, materialId, assemblyType));
}

export type StockDayLedger = { date: string; opening: number; received: number; issued: number; closing: number };

/** A day-by-day opening → received → issued → closing breakdown for one material, oldest first. */
export function buildDayLedger(
  orders: StockOrderInput[],
  issues: Pick<StockIssue, 'materialId' | 'materialAssemblyType' | 'qty' | 'date'>[],
  materialId: string,
  assemblyType?: string,
): StockDayLedger[] {
  const receipts = receiptsForMaterial(orders, materialId, assemblyType);
  const ownIssues = issues.filter((issue) => issue.materialId === materialId && matchesRow(issue.materialAssemblyType, assemblyType));
  const dates = Array.from(new Set([...receipts.map((r) => r.date), ...ownIssues.map((i) => i.date)])).sort();
  let running = 0;
  return dates.map((date) => {
    const received = round3(receipts.filter((r) => r.date === date).reduce((sum, r) => sum + r.qty, 0));
    const issued = round3(ownIssues.filter((i) => i.date === date).reduce((sum, i) => sum + num(i.qty), 0));
    const opening = running;
    running = round3(running + received - issued);
    return { date, opening, received, issued, closing: running };
  });
}

// ── Recording an issue ────────────────────────────────────────────────────
/** What's wrong with a draft issue, if anything; empty when it's ready to save. A quantity beyond the
 * current balance is flagged, not blocked — real usage sometimes runs ahead of what's on paper. */
export function validateStockIssue(qty: string, destination: StockDestination | null): string[] {
  const problems: string[] = [];
  if (!destination) problems.push('choose which labour used it');
  if (num(qty) <= 0) problems.push('enter a quantity');
  return problems;
}

export function overIssueWarning(qty: string, balance: number, unit: string): string {
  const n = num(qty);
  return n > 0 && n > balance ? `Only ${round3(balance)} ${unit} is on record as available — this will take the balance negative.` : '';
}

// ── Concrete grade suggestions (shown only while recording Cement's own usage) ──────────
export type SuggestedRatioIssue = { key: string; materialId: string; materialAssemblyType?: string; materialLabel: string; unit: string; qty: number };

/** Sand/Aggregate quantities a grade's ratios suggest for a given Cement quantity — a starting point to
 * edit or skip, never saved on its own. Ratios naming a material not on the stock ledger are dropped. */
export function suggestedRatioIssues(grade: ConcreteGrade, cementQty: number, tracked: TrackedMaterial[]): SuggestedRatioIssue[] {
  const byId = new Map(tracked.map((item) => [item.id, item]));
  const qty = num(cementQty);
  return grade.ratios
    .map((ratio): SuggestedRatioIssue | null => {
      const material = byId.get(ratio.materialId);
      if (!material) return null;
      return {
        key: material.id, materialId: material.rootId, materialAssemblyType: material.assemblyType,
        materialLabel: material.label, unit: material.unit, qty: round3(num(ratio.qtyPerCementUnit) * qty),
      };
    })
    .filter((row): row is SuggestedRatioIssue => row !== null);
}
